/**
 * @Author: v_jppjpeng
 * @Date: 2023-10-16 17:37:35
 * @Explain: 列表 - 上拉刷新
 */
import { intlGet } from '@/locales';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Checkbox, List, Loading } from 'tdesign-react';
// api
import { handleReturnResults, rewritePagingParams } from '@/api/interfaceUtils';
// utils
import { throttle } from '@/utils/common';
import { isObject } from '@/utils/common/dataJudgment';
import { dividedBy } from '@/utils/format/number';
// type
import { StatusTipEnum } from '@/definition/common/data.d';
import type { ScrollListProps } from '../data.d';
// components
import SearchBox from '../searchBox';

function ScrollList(props: ScrollListProps, refs: any) {
  const {
    dataKey,
    listKey = 'dataList',
    searchKey,
    displayTextKey = 'label',
    isAutoRequest = false,
    isResetSearch = false,
    multiple = false,
    emptyText: emptyTextStr = intlGet('COMMON.NO.DATA'),
    searchPlaceholder,
    footer,
    defaultSearchParams,
    defaultDataList = [],
    value,
    searchable = true,
    autofocus = true,
    listHeader,
    getFilterOptions,
    getList,
    onFilter,
    onChange,
    onClose,
    onReqSuccess,
    onReqEmpty,
    setIsResetSearch,
  } = props;
  // 状态
  const [reqStatus, setReqStatus] = useState<StatusTipEnum>(StatusTipEnum.init);
  const [isSearch, setIsSearch] = useState<boolean>(isAutoRequest);
  const [isDown, setIsDown] = useState<boolean>(false);
  // 搜索参数
  const [searchParams, setSearchParams] = useState({
    pageIndex: 1,
    pageSize: 10,
    ...defaultSearchParams,
  });
  // 设置搜索字段
  const setSearchValue = (params: any) => {
    const data = {
      ...searchParams,
      ...params,
    };
    setSearchParams(data);
  };
  // 分页参数
  const [pageConfig, setPageConfig] = useState<any>({ total: 0 });
  // 暂无数据 或 暂无更多数据
  const [emptyText, setEmptyText] = useState<string | ReactNode>(emptyTextStr);
  // 列表数据
  const [dataList, setDataList] = useState<any[]>(defaultDataList);

  const onText = (item: any) => item[displayTextKey];
  const getTitleText = (item: any) => {
    const text = isObject(item[displayTextKey])
      ? item.label
      : item[displayTextKey];
    const tips: any = intlGet(item.tipsKey) || item.tips || '';

    return tips ? tips : text;
  };

  let lastSearch = '';
  // 获取列表
  const getDataList = useCallback(async () => {
    const newParams = rewritePagingParams(searchParams);

    // 这里是为了解决连续输入,产生多次请求的问题
    // if (reqStatus === StatusTipEnum.loading) {
    //   if (
    //     searchKey &&
    //     newParams[searchKey] &&
    //     lastSearch !== newParams[searchKey]
    //   ) {
    //     setIsSearch(true);
    //   }
    //   return;
    // }
    setReqStatus(StatusTipEnum.loading);

    const result: any = await getList?.(newParams)?.finally(() =>
      setIsSearch(false),
    );
    lastSearch = searchKey && newParams[searchKey];

    handleReturnResults({
      params: result,
      successCallBack: (res) => {
        const { [listKey]: data_list = [], pageInfo } = dataKey
          ? res?.data[dataKey]
          : res?.data || {};
        const list = getFilterOptions?.(data_list || [], pageInfo) || [];

        if (pageInfo) {
          setPageConfig(pageInfo);
        }

        if (newParams.pageNum > 1) {
          setDataList((val) => [...val, ...list]);
        } else {
          setDataList(list);
        }

        setReqStatus(
          list?.length ? StatusTipEnum.success : StatusTipEnum.empty,
        );
      },
      errorCallBack: () => setReqStatus(StatusTipEnum.error),
    });
  }, [
    searchParams,
    reqStatus,
    setIsSearch,
    getList,
    listKey,
    dataKey,
    getFilterOptions,
  ]);

  // 滚动加载
  const onScroll = ({ scrollBottom }: { scrollBottom: number }) => {
    if (scrollBottom < 10) {
      setIsDown(true);
    }
  };

  // 搜索框
  const onSearchBox = () => {
    const onSearch = (searchValue: string) => {
      if (searchKey) {
        setDataList([]);
        setSearchValue({ pageIndex: 1, [searchKey]: searchValue });
      }
    };
    return (
      <SearchBox
        autofocus={autofocus}
        size="full"
        placeholder={searchPlaceholder}
        value={searchKey && (searchParams as any)?.[searchKey]}
        onClear={() => {
          setIsSearch(true);
        }}
        // onKeyup={() => {
        //   if (!isSearch) {
        //     throttle(() => {
        //       setIsSearch(true);
        //     }, 2000)();
        //   }
        // }}
        onKeydown={() => {
          throttle(() => {
            setIsSearch(false);
          }, 3000)();
        }}
        onSearch={(val) => {
          if (!isSearch) {
            onSearch(val);
            setIsSearch(true);
          }
        }}
        onChange={(val) => {
          onSearch(val);
          if (!isSearch) {
            throttle(() => {
              setIsSearch(true);
            }, 1000)();
          }
        }}
      />
    );
  };

  // 底部提示语相关
  const onBottomRender = () => {
    return (
      <>
        {/* 加载中 */}
        {reqStatus === StatusTipEnum.loading && (
          <li className="loading-text">
            <Loading
              loading
              text={`${intlGet('COMMON.LOADING')}...`}
              size="small"
            />
          </li>
        )}
        {/* 暂无数据 */}
        {reqStatus === StatusTipEnum.empty && (
          <li
            className={classNames('no-more-text', {
              'no-data-text': emptyText === intlGet('COMMON.NO.DATA'),
            })}
          >
            {emptyText}
          </li>
        )}
      </>
    );
  };

  // 列表
  const newList: any[] = useMemo(() => {
    if (onFilter) {
      return onFilter(dataList);
    }
    return dataList;
  }, [dataList, onFilter]);

  // 触发获取列表
  useEffect(() => {
    if (isSearch) {
      getDataList();
    }
  }, [isSearch]);

  // 重新获取列表
  useEffect(() => {
    if (isResetSearch) {
      setSearchValue({
        pageIndex: 1,
        pageSize: 10,
      });
      setIsSearch(true);
    }
    return () => setIsResetSearch?.(false);
  }, [isResetSearch]);

  // 上拉获取数据
  useEffect(() => {
    if (isDown) {
      const { pageIndex, pageSize } = searchParams;
      // 当前数据总条数共满足翻几页
      const totalPageIndex = Math.ceil(
        +dividedBy(pageConfig?.total || 0, pageSize),
      );

      const flag = pageIndex < totalPageIndex && !isSearch;
      // 判断存在数据&已全部加载
      const hasBeenTotal = pageIndex >= totalPageIndex && !isSearch;

      // 数据翻页
      if (flag) {
        setSearchValue({ pageIndex: searchParams.pageIndex + 1 });
        setIsSearch(true);
      }

      // 存在数据 & 已全部加载
      if (hasBeenTotal) {
        setEmptyText(intlGet('COMMON.NO.MORE.DATA'));
        setReqStatus(StatusTipEnum.empty);
      }

      // 无数据
      if (!pageConfig?.total) {
        setEmptyText(emptyTextStr);
        setReqStatus(StatusTipEnum.empty);
      }
    }
    return () => setIsDown(false);
  }, [isDown]);

  useEffect(() => {
    if (reqStatus === StatusTipEnum.success) {
      onReqSuccess?.(dataList);
    }
    if (reqStatus === StatusTipEnum.empty) {
      onReqEmpty?.(dataList, searchParams);
    }
  }, [reqStatus]);

  useEffect(() => {
    setEmptyText(emptyTextStr);
  }, [emptyTextStr]);

  useImperativeHandle(refs, () => ({
    searchValue: searchKey && (searchParams as any)?.[searchKey],
    setSearchValue,
    setIsSearch,
  }));
  return !isResetSearch ? (
    <>
      {/* 搜索 */}
      {searchable ? (
        <div className="dropdown-search-box">{onSearchBox()}</div>
      ) : null}

      {/* 列表 - 头部 */}
      {listHeader}

      {/* 列表 */}
      {multiple ? (
        <Checkbox.Group
          className="td-checkbox-group"
          value={value}
          onChange={(val, context) => {
            const currentVal = context.current;
            const currentItem = dataList.find(
              (item) => currentVal === item.value,
            );
            const selectedOptions = dataList.filter((item) =>
              val.includes(item.value),
            );

            onChange?.(val, {
              option: currentItem,
              selectedOptions,
              trigger: context.type,
              e: context.e as any,
            });
          }}
        >
          <List
            className="td-scroll-list"
            asyncLoading={onBottomRender()}
            onScroll={onScroll}
          >
            {newList.map((item) => (
              <li
                key={item.value}
                className="t-list-item"
                title={item?.tips ? item.tips : getTitleText(item)}
              >
                <div className="t-list-item-main">
                  <Checkbox value={item.value} disabled={item?.disabled}>
                    {onText(item)}
                  </Checkbox>
                </div>
              </li>
            ))}
          </List>
        </Checkbox.Group>
      ) : (
        <List className="td-scroll-list" onScroll={onScroll}>
          {newList.map((item) => (
            <li
              key={item.value}
              className={classNames('t-list-item', {
                'is-selected': value === item.value,
                'is-disabled': item.disabled,
              })}
              title={getTitleText(item)}
              onClick={(event) => {
                if (!item.disabled) {
                  const val = item.value;
                  onChange?.(val, {
                    option: item,
                    selectedOptions: item,
                    trigger: 'check',
                    e: event,
                  });
                  onClose?.();
                }
              }}
            >
              <div className="t-list-item-main">{onText(item)}</div>
            </li>
          ))}
          {/* 提示语 */}
          {onBottomRender()}
        </List>
      )}
      {/* 底部 */}
      {footer && <div className="dropdown-footer">{footer}</div>}
    </>
  ) : null;
}

export default forwardRef(ScrollList);
